// import necessary modules
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import Spacer from '@/Atoms/Spacer';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import React, {useEffect, useCallback, useMemo, useRef, useState} from 'react';
import {
  Switch,
  View,
  TouchableOpacity,
  StyleProp,
  ViewStyle,
} from 'react-native';
import StockfishEngine from '.';
import {styles} from './styles';
import Entypo from 'react-native-vector-icons/Entypo';
import SlideModal from '@/Atoms/SlideModal';
import CCButton from '@/Atoms/CCButton';
import Slider from '@react-native-community/slider';
import ConditionalRender from '@/Atoms/ConditionalRender';
import customStyles from '@/Screens/Games/styles';
import Tooltip from '@/Atoms/Tooltip';
import Ionicons from 'react-native-vector-icons/Ionicons';

type StockfishComponentProps = {
  fen: string;
  depth?: number;
  variations?: number;
  engineOn?: boolean;
  onResult?: (a: any, b: any) => void;
  count?: number;
  customStyle?: StyleProp<ViewStyle>;
  gameScoreDetails?: any | null;
  switchColor?: string;
  bgColor?: string;
  thumbErrorColor?: string;
  textColor?: string;
  reviewVariant?: boolean;
};

// Define the renderMoves function
const renderMoves = (moves, baseTurn, score, parentCount = 0) => {
  let count = parentCount;
  const elements: any = [];
  elements.push(
    <React.Fragment key="0">
      {score !== undefined && <CCText fontWeight="semibold">{score}</CCText>}
      <Spacer spacing={5} horizontal />
    </React.Fragment>,
  );
  if (baseTurn === 'b') {
    count = count - 1;
    if (moves?.length > 0) {
      elements.push(
        <React.Fragment key="0">
          <CCText>{++count}. ... </CCText>
          <CCText>{moves?.[0]}</CCText>
          <Spacer spacing={5} horizontal />
        </React.Fragment>,
      );
    }
    for (let i = 1; i < moves?.length; i += 2) {
      elements.push(
        <React.Fragment key={i}>
          <CCText>
            {`${++count}. `}
            {moves?.[i]}
          </CCText>
          {i + 1 < moves?.length && (
            <>
              <Spacer spacing={5} horizontal />
              <CCText>{moves?.[i + 1]}</CCText>
            </>
          )}
          <Spacer spacing={5} horizontal />
        </React.Fragment>,
      );
    }
  } else {
    for (let i = 0; i < moves?.length; i += 2) {
      elements.push(
        <React.Fragment key={i}>
          <CCText>
            {`${++count}. `}
            {moves?.[i]}
          </CCText>
          {i + 1 < moves?.length && (
            <>
              <Spacer spacing={5} horizontal />
              <CCText>{moves?.[i + 1]}</CCText>
            </>
          )}
          <Spacer spacing={5} horizontal />
        </React.Fragment>,
      );
    }
  }
  return elements;
};

// Define the MoveSequencesDisplay component
const MoveSequencesDisplay = ({moveSequences, count, baseTurnProp}) => {
  const [showFull, setShowFull] = useState({});

  useEffect(() => {
    if (moveSequences) {
      setShowFull(
        Object.keys(moveSequences).reduce((acc, key) => {
          acc[key] = false;
          return acc;
        }, {}),
      );
    }
  }, [moveSequences]);

  const sortedEntries = useMemo(
    () =>
      Object.entries(moveSequences).sort((a, b) => {
        const scoreA = a[1]?.score;
        const scoreB = b[1]?.score;

        // Check if scores are mate indicators (e.g., "M1#", "M2#") and prioritize mates
        const isMateA = typeof scoreA === 'string' && scoreA.startsWith('M');
        const isMateB = typeof scoreB === 'string' && scoreB.startsWith('M');

        if (isMateA && isMateB) {
          if (baseTurnProp === 'w') {
            // For Black, sort mates in descending order (M3# before M2#)
            return parseInt(scoreB.slice(1)) - parseInt(scoreA.slice(1));
          } else {
            // For White, sort mates in ascending order (M1# before M2#)
            return parseInt(scoreA.slice(1)) - parseInt(scoreB.slice(1));
          }
        } else if (isMateA) {
          return -1;
        } else if (isMateB) {
          return 1;
        } else if (scoreA !== undefined && scoreB !== undefined) {
          return baseTurnProp === 'b' ? scoreA - scoreB : scoreB - scoreA;
        }

        return 0;
      }),
    [baseTurnProp, moveSequences],
  );

  return (
    <View style={{ marginTop: 4 }}>
      {sortedEntries.map(([variationNumber, {moves, baseTurn, score}]) => (
        <Box
          key={variationNumber}
          style={[score !== undefined ? styles.eachVariation : {}]}>
          <Box type="row-flex-start" style={{ flex: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {score !== undefined && (
              <View style={{
                backgroundColor: 'rgba(107, 88, 77, 0.08)',
                paddingHorizontal: 6,
                paddingVertical: 2,
                borderRadius: 4,
                marginRight: 8,
                alignSelf: 'center',
              }}>
                <CCText fontSize={11} fontWeight="bold" color="#4D3F37">{score}</CCText>
              </View>
            )}
            <CCText
              {...(showFull?.[variationNumber] ? {} : {numberOfLines: 1})}
              style={[showFull?.[variationNumber] ? {} : {flex: 0.89}, {flexDirection: 'row', flexWrap: 'wrap', flex: 1}]}>
              {renderMoves(moves, baseTurn, undefined, count)}
            </CCText>
          </Box>
          {!showFull?.[variationNumber] && score !== undefined && (
            <Box type="row-flex-end" style={{width: 24, justifyContent: 'center', alignItems: 'center'}}>
              <Entypo
                name="chevron-small-down"
                size={20}
                color={CCColors?.Black}
                onPress={() =>
                  setShowFull(prev => ({...prev, [variationNumber]: true}))
                }
              />
            </Box>
          )}
        </Box>
      ))}
    </View>
  );
};

// Define the StockfishComponent
const StockfishComponent = ({
  fen,
  depth = 30,
  variations = 3,
  count,
  engineOn,
  onResult,
  customStyle,
  gameScoreDetails = true,
  switchColor,
  bgColor,
  thumbErrorColor,
  textColor,
  reviewVariant = false,
}: StockfishComponentProps) => {
  const [enableStockfish, setEnableStockfish] = useState(engineOn ?? false);
  const [moveSequences, setMoveSequences] = useState({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [settings, setSettings] = useState({depth: 30, variations: 3});
  const [currentVariations, setCurrentVariations] = useState(variations);
  const [currentDepth, setCurrentDepth] = useState(depth);
  const [baseTurnState, setBaseTurnState] = useState('w');

  const [nextBestMove, setNextBestMove] = useState<string | null>(null);
  const [opponentThreat, setOpponentThreat] = useState<string | null>(null);

  const threatFen = useMemo(() => {
    if (!fen) return '';
    const parts = fen.split(' ');
    if (parts.length < 2) return fen;
    parts[1] = parts[1] === 'w' ? 'b' : 'w';
    if (parts.length > 3) parts[3] = '-';
    return parts.join(' ');
  }, [fen]);

  useEffect(() => {
    if (engineOn !== undefined) {
      setEnableStockfish(engineOn);
    }
  }, [engineOn]);

  useEffect(() => {
    setNextBestMove(null);
    setOpponentThreat(null);
  }, [fen]);

  useEffect(() => {
    if (!enableStockfish) {
      setNextBestMove(null);
      setOpponentThreat(null);
    }
  }, [enableStockfish]);

  const handleToggleEngine = (value: boolean) => {
    setEnableStockfish(value);
    if (onResult) {
      onResult(null, value);
    }
  };

  const handleThreatResult = useCallback((variationNumber, moves) => {
    if (variationNumber === 1) {
      // moves[] is already SAN from parser.ts
      const sanMove = moves && moves.length > 0 ? moves[0] : null;
      if (sanMove) setOpponentThreat(sanMove);
    }
  }, []);

  const handleStockfishResult = (
    variationNumber,
    moves,
    baseTurnTemp,
    extractedScore,
    isMate,
    rawData,
  ) => {
    // moves[] is already SAN from the new engine (parser.ts converts UCI -> SAN).
    const sanMovesOnly = Array.isArray(moves) ? moves : [];
    setBaseTurnState(baseTurnTemp);

    // score arrives as a raw centipawn string (e.g. "34") — divide by 100.
    let displayScore;
    if (isMate) {
      const mateMoves = parseInt(extractedScore, 10);
      if (mateMoves === 0) {
        displayScore = '#';
      } else {
        displayScore = `M${Math.abs(mateMoves)}#`;
      }
    } else {
      let cp = Number(extractedScore);
      if (baseTurnTemp === 'b') cp = cp * -1;
      displayScore = (cp / 100).toFixed(2);
    }

    if (variationNumber === 1) {
      const sanMove = sanMovesOnly.length > 0 ? sanMovesOnly[0] : null;
      if (sanMove) setNextBestMove(sanMove);
    }

    setMoveSequences(prev => {
      const newResult = {
        ...prev,
        [variationNumber]: {
          moves: sanMovesOnly,
          score: displayScore,
          baseTurn: baseTurnTemp,
        },
        baseTurn: baseTurnTemp,
      };
      
      if (onResult && variationNumber === 1) {
        onResult({
          score: displayScore,
          isMate,
          baseTurn: baseTurnTemp,
          rawScore: extractedScore
        }, enableStockfish);
      }

      return newResult;
    });
  };

  const handleSaveSettings = () => {
    setMoveSequences({});
    setIsModalVisible(false);
    setSettings({depth: currentDepth, variations: currentVariations});
  };

  return (
    <>
      {reviewVariant ? (
        <View style={styles.reviewEngineBox}>
          <View style={styles.reviewEngineIcon}>
            <Ionicons name="trending-up" size={18} color="#2F8E83" />
          </View>
          <View style={styles.reviewEngineText}>
            <CCText style={styles.reviewEngineTitle}>Engine evaluation</CCText>
            <CCText style={styles.reviewEngineSub}>
              Scores and timeline shown
            </CCText>
          </View>
          <Switch
            trackColor={{
              false: 'rgba(77,63,55,0.10)',
              true: 'rgba(61,171,158,0.35)',
            }}
            thumbColor={enableStockfish ? '#3DAB9E' : '#FFFFFF'}
            onValueChange={() => setEnableStockfish(!enableStockfish)}
            value={enableStockfish}
            disabled={!gameScoreDetails}
          />
          <TouchableOpacity onPress={() => setIsModalVisible(true)}>
            <Entypo name="cog" size={22} color="#4D3F37" />
          </TouchableOpacity>
        </View>
      ) : !gameScoreDetails ? (
        <Tooltip
          text="Please wait while we are analyzing your game details!"
          children={toggleEvent => (
            <View
              onPointerEnter={toggleEvent}
              onPointerLeave={toggleEvent}
              style={{opacity: 0.6}}>
              <Box
                type="row-center-between"
                style={[
                  styles.switchBox,
                  customStyle,
                  enableStockfish
                    ? {
                        backgroundColor: 'rgba(61, 171, 158, 0.06)',
                        borderColor: 'rgba(61, 171, 158, 0.2)',
                        borderWidth: 1,
                      }
                    : {
                        backgroundColor: 'rgba(107, 88, 77, 0.03)',
                        borderColor: 'rgba(107, 88, 77, 0.08)',
                        borderWidth: 1,
                      },
                ]}>
                <Box type="row-flex-start">
                  <Switch
                    trackColor={{
                      false: 'rgba(107, 88, 77, 0.16)',
                      true: '#3DAB9E',
                    }}
                    thumbColor={'#FFFFFF'}
                    onValueChange={handleToggleEngine}
                    value={enableStockfish}
                    disabled={!gameScoreDetails}
                  />

                  <Spacer spacing={10} horizontal />
                  <CCText
                    fontSize={13}
                    fontWeight="semibold"
                    color={enableStockfish ? '#3DAB9E' : '#6b584d'}>
                    Engine Evaluation
                  </CCText>
                </Box>
                <Spacer spacing={10} horizontal />
                <TouchableOpacity
                  onPress={() => setIsModalVisible(true)}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 14,
                    backgroundColor: 'rgba(107, 88, 77, 0.05)',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <Entypo name="cog" size={16} color="#6b584d" />
                </TouchableOpacity>
              </Box>
            </View>
          )}
        />
      ) : (
        <Box
          type="row-center-between"
          style={[
            styles.switchBox,
            customStyle,
            enableStockfish
              ? {
                  backgroundColor: '#EEF9F7',
                  borderColor: 'rgba(0, 163, 137, 0.15)',
                  borderWidth: 1,
                }
              : {
                  backgroundColor: '#FFFFFF',
                  borderColor: 'rgba(107, 88, 77, 0.12)',
                  borderWidth: 1,
                },
          ]}>
          <Box type="row-flex-start">
            <Switch
              trackColor={{
                false: 'rgba(107, 88, 77, 0.16)',
                true: '#3DAB9E',
              }}
              thumbColor={'#FFFFFF'}
              onValueChange={handleToggleEngine}
              value={enableStockfish}
              disabled={!gameScoreDetails}
            />
            <Spacer spacing={10} horizontal />
            <CCText
              fontSize={13}
              fontWeight="semibold"
              color={enableStockfish ? '#3DAB9E' : '#6b584d'}>
              Engine Evaluation
            </CCText>
          </Box>
          <Spacer spacing={10} horizontal />
          <TouchableOpacity
            onPress={() => setIsModalVisible(true)}
            style={{
              width: 28,
              height: 28,
              borderRadius: 14,
              backgroundColor: 'rgba(107, 88, 77, 0.05)',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Entypo name="cog" size={16} color="#6b584d" />
          </TouchableOpacity>
        </Box>
      )}
      {!enableStockfish && <Spacer spacing={5} />}

      {enableStockfish && (
        <Box style={{
          marginTop: 8,
          marginBottom: 12,
          padding: 10,
          borderRadius: 10,
          backgroundColor: '#FAF6EB',
          borderWidth: 1,
          borderColor: '#EAE2D5',
        }}>
          <Box style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
            <CCText fontSize={12} fontWeight="bold" color="#4D3F37">Next Best Move:</CCText>
            <CCText fontSize={12} fontWeight="bold" color={CCColors.Green}>{nextBestMove || 'Calculating...'}</CCText>
          </Box>
          <Box style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <CCText fontSize={12} fontWeight="bold" color="#4D3F37">Opponent Threat:</CCText>
            <CCText fontSize={12} fontWeight="bold" color={CCColors.Red}>{opponentThreat || 'Calculating...'}</CCText>
          </Box>
        </Box>
      )}

      <StockfishEngine
        fen={fen}
        depth={settings.depth}
        variations={settings.variations}
        engineOn={enableStockfish}
        onResult={handleStockfishResult}
      />
      <StockfishEngine
        fen={threatFen}
        depth={12}
        variations={1}
        engineOn={enableStockfish}
        onResult={handleThreatResult}
      />
      <ConditionalRender
        condition={enableStockfish}
        childrenA={
          <MoveSequencesDisplay
            moveSequences={moveSequences}
            count={count}
            baseTurnProp={baseTurnState}
          />
        }
      />

      <SlideModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        modalHeaderTitle="Evaluation Settings">
        <Box>
          <CCText>Depth ({currentDepth}/37)</CCText>
          <Slider
            minimumValue={1}
            maximumValue={37}
            step={1}
            value={currentDepth}
            onValueChange={value => setCurrentDepth(value)}
          />
          <CCText>Variations ({currentVariations}/5)</CCText>
          <Slider
            minimumValue={1}
            maximumValue={5}
            step={1}
            value={currentVariations}
            onValueChange={value => setCurrentVariations(value)}
          />
          <Spacer spacing={20} />
          <CCButton onPress={handleSaveSettings}>Save</CCButton>
        </Box>
      </SlideModal>
    </>
  );
};

function sortGamesByScore(games) {
  if (games.baseTurn === 'b') {
    // Extract the games excluding the baseTurn key
    let gameEntries = Object.entries(games).filter(
      ([key, value]) => key !== 'baseTurn',
    );

    // Sort the games by score
    gameEntries.sort((a, b) => a[1].score - b[1].score);

    // Reconstruct the sorted object
    let sortedGames = {baseTurn: games.baseTurn};
    gameEntries.forEach(([key, value]) => {
      sortedGames[key] = value;
    });

    return sortedGames;
  } else {
    return games;
  }
}

export default StockfishComponent;
